import React from 'react';
import { Link } from 'react-router-dom';
import { 
  EnvelopeIcon, 
  MapPinIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  PhoneIcon,
  ArrowUpRightIcon
} from '@heroicons/react/24/outline';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const companyLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'Services', href: '/services' },
    { name: 'News & Research', href: '/news' },
  ];

  const tradingLinks = [
    { name: 'Trading Platform', href: '/trading' },
    { name: 'Copy Trading', href: '/copy-trading' },
    { name: 'Bots Trading', href: '/bots-trading' },
    { name: 'Live Trading', href: '/live-trading' },
    { name: 'P2P Trading', href: '/p2p-trading' },
    { name: 'Markets', href: '/markets' },
    { name: 'Portfolio', href: '/portfolio' },
  ];

  const resourceLinks = [
    { name: 'Account', href: '/account' },
    { name: 'Sign In', href: '/login' },
    { name: 'Open Account', href: '/register' },
    { name: 'Contact Support', href: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms of Service', href: '/terms-of-service' },
    { name: 'Risk Disclosure', href: '/risk-disclosure' },
    { name: 'Regulatory Information', href: '/regulatory-information' },
  ];

  const trustBadges = [
    {
      icon: ShieldCheckIcon,
      title: 'Bank-Level Security',
      description: 'Your funds and data protected with industry-leading encryption and security measures.',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Competitive Pricing',
      description: 'Transparent fee structure with no hidden costs and institutional-grade pricing.',
    },
    {
      icon: ChartBarIcon,
      title: 'Professional Tools',
      description: 'Institutional-grade trading tools with real-time market analysis and insights.',
    },
  ];

  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Trust Badges */}
        <div className="py-14 border-b border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustBadges.map((badge, index) => (
              <div 
                key={index} 
                className="group flex items-start space-x-4 p-6 bg-slate-900/60 rounded-xl border border-slate-800 hover:border-amber-500/30 transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center border border-amber-500/20 group-hover:border-amber-500/40 transition-all duration-300">
                  <badge.icon className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white mb-1.5 group-hover:text-amber-300 transition-colors duration-300">
                    {badge.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {badge.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-14 border-b border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="mb-6">
                <img 
                  src="/intracapital-logos/intracapital-logo-white.png" 
                  alt="INTRA CAPITAL LIMITED" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-slate-400 mb-6 text-sm leading-relaxed max-w-sm">
                INTRA CAPITAL LIMITED is a professional investment and trading services firm 
                committed to delivering institutional-grade financial solutions to clients worldwide.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <a href="mailto:support@intracapitals.com" className="flex items-center space-x-3 group">
                  <EnvelopeIcon className="h-4 w-4 text-slate-500 group-hover:text-amber-400 transition-colors flex-shrink-0" />
                  <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">support@intracapitals.com</span>
                </a>
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-400">Britannia House, 503 1-11 Glenthorne Road, London, England, W6 0LH</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mt-6">
                <a 
                  href="http://dashboard.intracapitals.com/"
                  className="inline-flex items-center px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200"
                >
                  Open Account
                  <ArrowUpRightIcon className="ml-1.5 h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Links Sections */}
            <div>
              <h3 className="text-xs font-semibold text-slate-300 tracking-widest uppercase mb-5">
                Company
              </h3>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-slate-400 hover:text-amber-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-slate-300 tracking-widest uppercase mb-5">
                Trading
              </h3>
              <ul className="space-y-3">
                {tradingLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-slate-400 hover:text-amber-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-slate-300 tracking-widest uppercase mb-5">
                Resources
              </h3>
              <ul className="space-y-3">
                {resourceLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-slate-400 hover:text-amber-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-slate-300 tracking-widest uppercase mb-5">
                Legal
              </h3>
              <ul className="space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-sm text-slate-400 hover:text-amber-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="py-8 border-b border-slate-800">
          <div className="flex flex-wrap justify-center gap-12">
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-400 mb-1">24/7</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Global Support</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-400 mb-1">99.9%</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-sky-400 mb-1">150+</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Countries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-violet-400 mb-1">500K+</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Active Clients</div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-xs text-slate-500 hover:text-amber-400 transition-colors duration-200"
                >
                  {link.name}
                </Link>
              ))}
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-xs text-slate-500">
                © {currentYear} INTRA CAPITAL LIMITED. All rights reserved.
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Registered in England & Wales. Financial services regulated by the FCA.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
